import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Heart, ArrowLeft, Users, Target, Calendar, Loader2, ExternalLink, RefreshCw, QrCode, Building, Wallet, Landmark, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function PublicDonation() {
  const { campaignId } = useParams();
  const subdomain = useStoreSubdomain();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDonateDialog, setShowDonateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    donor_name: '',
    donor_email: '',
    donor_phone: '',
    amount: 0,
    message: '',
    is_anonymous: false,
    payment_method: 'qris',
    is_recurring: false,
    frequency: 'monthly'
  });

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const res = await api.get(`/store/${subdomain}/donations/${campaignId}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaign();
  }, [subdomain, campaignId]);

  const handleDonate = async () => {
    if (!form.donor_name || !form.donor_email || !form.amount) {
      toast.error('Lengkapi data donasi');
      return;
    }
    if (form.amount < 1000) {
      toast.error('Minimum donasi Rp 1.000');
      return;
    }
    
    setSubmitting(true);
    try {
      if (form.is_recurring) {
        // Create recurring donation
        await api.post(`/public/${subdomain}/donations/recurring`, {
          campaign_id: campaignId,
          amount: form.amount,
          frequency: form.frequency,
          donor_name: form.donor_name,
          donor_email: form.donor_email,
          is_anonymous: form.is_anonymous
        });
        toast.success(`Donasi recurring ${form.frequency === 'weekly' ? 'mingguan' : form.frequency === 'monthly' ? 'bulanan' : 'tahunan'} berhasil dibuat!`);
        setShowDonateDialog(false);
      } else {
        const res = await api.post(`/store/${subdomain}/donate`, {
          campaign_id: campaignId,
          ...form
        });
        
        if (res.data.payment_url && form.payment_method !== 'bank_transfer') {
          window.open(res.data.payment_url, '_blank');
        }
        setShowDonateDialog(false);
        const donId = res.data.donation?.id || res.data.donation_id;
        if (donId) {
          navigate(`/store/${subdomain}/thank-you?donation=${donId}&type=donation`);
        } else {
          toast.success('Donasi berhasil dikirim!');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memproses donasi');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const presetAmounts = data?.campaign?.preset_amounts || [10000, 25000, 50000, 100000, 250000, 500000];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-pink-500" />
      </div>
    );
  }

  if (!data || !data.campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 flex items-center justify-center">
        <div className="text-center">
          <Heart size={48} className="mx-auto text-pink-300 mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Campaign tidak ditemukan</h1>
          <Link to={`/store/${subdomain}`} className="text-pink-600 hover:underline">Kembali ke Store</Link>
        </div>
      </div>
    );
  }

  const { campaign, recent_donors, tenant } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100" data-testid="public-donation-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-pink-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} />
            <span className="font-medium">{tenant?.name || subdomain}</span>
          </Link>
          <Button onClick={() => setShowDonateDialog(true)} className="bg-pink-500 hover:bg-pink-600" data-testid="donate-now-btn">
            <Heart size={16} className="mr-2" /> Donasi Sekarang
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaign Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-48 bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                {campaign.thumbnail ? (
                  <img src={campaign.thumbnail} alt={campaign.title} className="w-full h-full object-cover" />
                ) : (
                  <Heart size={64} className="text-white/50" />
                )}
              </div>
              <CardContent className="p-6">
                <h1 className="text-2xl font-bold text-slate-900 mb-3">{campaign.title}</h1>
                
                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between items-end mb-2">
                    <div>
                      <p className="text-2xl font-bold text-pink-600">Rp {fmt(campaign.total_raised)}</p>
                      <p className="text-sm text-slate-500">terkumpul dari Rp {fmt(campaign.target_amount)}</p>
                    </div>
                    <span className="text-lg font-semibold text-pink-600">{campaign.progress_percent}%</span>
                  </div>
                  <Progress value={campaign.progress_percent} className="h-3 bg-pink-100" />
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 py-4 border-t border-slate-100">
                  <div className="text-center">
                    <Users size={20} className="mx-auto text-pink-400 mb-1" />
                    <p className="text-lg font-bold text-slate-900">{campaign.donor_count}</p>
                    <p className="text-xs text-slate-500">Donatur</p>
                  </div>
                  <div className="text-center">
                    <Target size={20} className="mx-auto text-pink-400 mb-1" />
                    <p className="text-lg font-bold text-slate-900">Rp {fmt(campaign.target_amount)}</p>
                    <p className="text-xs text-slate-500">Target</p>
                  </div>
                  <div className="text-center">
                    <Calendar size={20} className="mx-auto text-pink-400 mb-1" />
                    <p className="text-lg font-bold text-slate-900">{campaign.deadline ? new Date(campaign.deadline).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}</p>
                    <p className="text-xs text-slate-500">Deadline</p>
                  </div>
                </div>
                
                {/* Description */}
                <div className="pt-4 border-t border-slate-100">
                  <h3 className="font-semibold text-slate-900 mb-2">Tentang Campaign</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{campaign.description}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Donate */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Donasi Cepat</h3>
                <div className="grid grid-cols-2 gap-2">
                  {presetAmounts.map((amt) => (
                    <Button
                      key={amt}
                      variant="outline"
                      className="border-pink-200 hover:bg-pink-50 hover:border-pink-300"
                      onClick={() => {
                        setForm({ ...form, amount: amt });
                        setShowDonateDialog(true);
                      }}
                      data-testid={`quick-donate-${amt}`}
                    >
                      Rp {fmt(amt)}
                    </Button>
                  ))}
                </div>
                <Button onClick={() => setShowDonateDialog(true)} className="w-full mt-4 bg-pink-500 hover:bg-pink-600">
                  <Heart size={16} className="mr-2" /> Nominal Lainnya
                </Button>
              </CardContent>
            </Card>

            {/* Recent Donors */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Donatur Terbaru</h3>
                <div className="space-y-3">
                  {recent_donors?.length > 0 ? recent_donors.map((d, i) => (
                    <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                        <Heart size={14} className="text-pink-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{d.is_anonymous ? 'Anonymous' : d.donor_name}</p>
                        <p className="text-sm text-pink-600 font-semibold">Rp {fmt(d.amount)}</p>
                        {d.message && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{d.message}</p>}
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-400 text-center py-4">Jadilah donatur pertama!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Donate Dialog */}
      <Dialog open={showDonateDialog} onOpenChange={setShowDonateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart size={20} className="text-pink-500" /> Donasi untuk {campaign.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Lengkap</Label>
              <Input 
                value={form.donor_name} 
                onChange={(e) => setForm({ ...form, donor_name: e.target.value })} 
                placeholder="Nama Anda"
                data-testid="donor-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={form.donor_email} 
                onChange={(e) => setForm({ ...form, donor_email: e.target.value })} 
                placeholder="email@example.com"
                data-testid="donor-email-input"
              />
            </div>
            <div>
              <Label>No. WhatsApp (Opsional)</Label>
              <Input 
                value={form.donor_phone} 
                onChange={(e) => setForm({ ...form, donor_phone: e.target.value })} 
                placeholder="08xxxxxxxxxx"
              />
            </div>
            <div>
              <Label>Nominal Donasi</Label>
              <Input 
                type="number"
                value={form.amount} 
                onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value) || 0 })} 
                placeholder="Minimum Rp 1.000"
                data-testid="donation-amount-input"
              />
              <div className="flex gap-2 mt-2">
                {[25000, 50000, 100000].map((amt) => (
                  <Button
                    key={amt}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setForm({ ...form, amount: amt })}
                  >
                    {fmt(amt)}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Pesan / Doa (Opsional)</Label>
              <Textarea 
                value={form.message} 
                onChange={(e) => setForm({ ...form, message: e.target.value })} 
                placeholder="Semoga bermanfaat..."
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Sembunyikan nama saya (Anonymous)</Label>
              <Switch 
                checked={form.is_anonymous} 
                onCheckedChange={(v) => setForm({ ...form, is_anonymous: v })} 
              />
            </div>

            {/* Payment Method */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <CreditCard size={16} className="text-pink-500" /> Metode Pembayaran
              </Label>
              <RadioGroup
                value={form.payment_method}
                onValueChange={(v) => setForm({ ...form, payment_method: v })}
                className="grid grid-cols-2 gap-2"
              >
                <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                  form.payment_method === 'qris' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                }`}>
                  <RadioGroupItem value="qris" />
                  <QrCode size={16} />
                  <span className="font-medium">QRIS</span>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                  form.payment_method === 'va' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                }`}>
                  <RadioGroupItem value="va" />
                  <Building size={16} />
                  <span className="font-medium">Virtual Account</span>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                  form.payment_method === 'ewallet' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                }`}>
                  <RadioGroupItem value="ewallet" />
                  <Wallet size={16} />
                  <span className="font-medium">E-Wallet</span>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                  form.payment_method === 'bank_transfer' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                }`}>
                  <RadioGroupItem value="bank_transfer" />
                  <Landmark size={16} />
                  <span className="font-medium">Transfer Bank</span>
                </label>
              </RadioGroup>
            </div>
            <div className="border rounded-lg p-3 bg-pink-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw size={16} className="text-pink-500" />
                  <Label className="font-medium">Donasi Recurring</Label>
                </div>
                <Switch 
                  checked={form.is_recurring} 
                  onCheckedChange={(v) => setForm({ ...form, is_recurring: v })} 
                />
              </div>
              {form.is_recurring && (
                <div>
                  <Label className="text-sm">Frekuensi</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Mingguan</SelectItem>
                      <SelectItem value="monthly">Bulanan</SelectItem>
                      <SelectItem value="yearly">Tahunan</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Donasi Rp {fmt(form.amount)} akan otomatis diulang setiap {form.frequency === 'weekly' ? 'minggu' : form.frequency === 'monthly' ? 'bulan' : 'tahun'}
                  </p>
                </div>
              )}
            </div>
            <Button 
              onClick={handleDonate} 
              disabled={submitting} 
              className="w-full bg-pink-500 hover:bg-pink-600"
              data-testid="submit-donation-btn"
            >
              {submitting && <Loader2 size={16} className="animate-spin mr-2" />}
              <Heart size={16} className="mr-2" /> Donasi Rp {fmt(form.amount)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
